import React, { useEffect, useMemo, useState } from "react";
import { X, Calendar as CalIcon, CheckSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { calendarApi, tasksApi } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Mobile "+" quick-create sheet for the tech Calendar page.
 * Lets the tech create a calendar EVENT or a TASK with the minimum fields
 * that make sense on a small screen. Uses the user's personal calendar
 * automatically for events and self-assigns tasks by default.
 */

const pad = (n) => String(n).padStart(2, "0");

// "YYYY-MM-DDTHH:mm" (local, no timezone) — the format datetime-local wants.
const toLocalInput = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
};

// "YYYY-MM-DD" for <input type="date">.
const toDateInput = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

export default function MobileCreateModal({ defaultDate, onClose, onCreated }) {
  const { user } = useAuth();

  // Default to selected day at 9:00 → 10:00 for events, day only for tasks.
  const baseDate = useMemo(() => {
    const d = defaultDate ? new Date(defaultDate) : new Date();
    d.setHours(9, 0, 0, 0);
    return d;
  }, [defaultDate]);

  const [kind, setKind] = useState("event"); // "event" | "task"
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  // Event fields
  const [start, setStart] = useState(toLocalInput(baseDate));
  const [end, setEnd] = useState(toLocalInput(new Date(baseDate.getTime() + 3600000)));
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState("");
  // Task fields
  const [dueAt, setDueAt] = useState(toDateInput(baseDate));
  // Backing data
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Fetch calendars once and pre-select the user's personal one.
  useEffect(() => {
    let cancelled = false;
    calendarApi.listCalendars()
      .then((rows) => {
        if (cancelled) return;
        setCalendars(rows || []);
        const personal = (rows || []).find((c) => c.isPersonal) || (rows || [])[0];
        if (personal) setCalendarId(personal.id);
      })
      .catch(() => { /* non-blocking — the user just won't see events fallback */ });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError(kind === "event" ? "Event title is required" : "Task title is required"); return; }
    setBusy(true);
    setError("");
    try {
      if (kind === "event") {
        if (!calendarId) { setError("No calendar available. Try again in a moment."); setBusy(false); return; }
        await calendarApi.createEvent({
          calendarId,
          title: title.trim(),
          description: notes,
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          allDay,
          location,
          busy: "busy",
          privacy: "default",
          notifications: [{ minutesBefore: 30 }],
        });
        toast.success("Event created");
      } else {
        await tasksApi.create({
          title: title.trim(),
          notes,
          assigneeIds: user?.id ? [user.id] : [],
          dueAt: dueAt || null,
        });
        toast.success("Task created");
      }
      onCreated && onCreated(kind);
    } catch (err) {
      setError(err?.response?.data?.detail || `Could not create ${kind}`);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center"
      style={{ backgroundColor: "rgba(15,23,42,0.55)" }}
      onClick={onClose}
      data-testid="mobile-create-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200"
        style={{ color: "#0f172a" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="text-[16px] font-bold" style={{ color: "#0B1E3F" }}>New</div>
          <button
            onClick={onClose}
            data-testid="mobile-create-close"
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" color="#475569" />
          </button>
        </div>

        {/* Type toggle */}
        <div className="px-5 pt-4">
          <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-slate-100">
            <button
              type="button"
              onClick={() => setKind("event")}
              data-testid="mobile-create-kind-event"
              className={`h-10 rounded-full text-[13.5px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${kind === "event" ? "bg-white shadow text-blue-600" : "text-slate-600"}`}
            >
              <CalIcon className="h-4 w-4" /> Event
            </button>
            <button
              type="button"
              onClick={() => setKind("task")}
              data-testid="mobile-create-kind-task"
              className={`h-10 rounded-full text-[13.5px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${kind === "task" ? "bg-white shadow text-blue-600" : "text-slate-600"}`}
            >
              <CheckSquare className="h-4 w-4" /> Task
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <div>
            <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Title</div>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={kind === "event" ? "e.g. Site visit — 12 Oak Ave" : "e.g. Order replacement filter"}
              data-testid="mobile-create-title"
              className="w-full h-11 px-3 rounded-lg border text-[15px]"
              style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
            />
          </div>

          {kind === "event" ? (
            <>
              <label className="flex items-center gap-2 text-[13px] font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={allDay}
                  onChange={(e) => setAllDay(e.target.checked)}
                  data-testid="mobile-create-allday"
                  className="h-4 w-4"
                />
                All day
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Start</div>
                  <input
                    type={allDay ? "date" : "datetime-local"}
                    value={allDay ? start.slice(0, 10) : start}
                    onChange={(e) => setStart(allDay ? `${e.target.value}T00:00` : e.target.value)}
                    data-testid="mobile-create-start"
                    className="w-full h-11 px-3 rounded-lg border text-[14px]"
                    style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
                  />
                </div>
                <div>
                  <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">End</div>
                  <input
                    type={allDay ? "date" : "datetime-local"}
                    value={allDay ? end.slice(0, 10) : end}
                    onChange={(e) => setEnd(allDay ? `${e.target.value}T23:59` : e.target.value)}
                    data-testid="mobile-create-end"
                    className="w-full h-11 px-3 rounded-lg border text-[14px]"
                    style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
                  />
                </div>
              </div>
              <div>
                <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Location (optional)</div>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Address, room, or link"
                  data-testid="mobile-create-location"
                  className="w-full h-11 px-3 rounded-lg border text-[14px]"
                  style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
                />
              </div>
              {calendars.length > 1 && (
                <div>
                  <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Calendar</div>
                  <select
                    value={calendarId}
                    onChange={(e) => setCalendarId(e.target.value)}
                    data-testid="mobile-create-calendar"
                    className="w-full h-11 px-3 rounded-lg border text-[14px]"
                    style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
                  >
                    {calendars.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.isPersonal ? " (Personal)" : ""}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          ) : (
            <div>
              <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Due date</div>
              <input
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                data-testid="mobile-create-due"
                className="w-full h-11 px-3 rounded-lg border text-[14px]"
                style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
              />
            </div>
          )}

          <div>
            <div className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Notes (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add details…"
              data-testid="mobile-create-notes"
              className="w-full px-3 py-2 rounded-lg border text-[14px] resize-y"
              style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff", color: "#0f172a" }}
            />
          </div>

          {error && (
            <div className="px-3 py-2 rounded-md text-[13px]" style={{ backgroundColor: "#fee2e2", border: "1px solid #fecaca", color: "#b91c1c" }} data-testid="mobile-create-error">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              data-testid="mobile-create-cancel"
              className="flex-1 h-12 rounded-lg border text-[14px] font-semibold"
              style={{ borderColor: "#cbd5e1", color: "#475569", backgroundColor: "#ffffff" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="mobile-create-submit"
              className="flex-[2] h-12 rounded-lg bg-blue-600 text-white text-[14px] font-bold disabled:opacity-60 flex items-center justify-center gap-1.5"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Saving…" : (kind === "event" ? "Create event" : "Create task")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
